import {handleTracking, getKeyFromPath, getUbaSource} from '@jobseeker/common-core/utils';
import {TrackEventUBA} from '@jobseeker/common-core/utils/analytics';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {useSelector} from 'react-redux';

import type {AppState} from '@jobseeker/common-core/types';
import {CSSProperties, useState} from 'react';
import subscriptionService from '@jobseeker/common-core/services/subscription.service';
import {PaidProducts, PaymentGateway} from '../../constant';
import dynamic from 'next/dynamic';
import Loader from '@jobseeker/lotus/components/Loader';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {VersionVariants} from '@jobseeker/lotus/elements';
import {useEffect} from 'react';
import {initiatePaymentGatewayApiResponse} from '@jobseeker/common-core/services/types';
import {Typography} from '@mui/material';
import {useRouter} from 'next/router';

type PayuFormType = {
  title: string;
  refCode: string;
  pageId: string;
  tracking: {
    action: string;
    category: string;
  };
  sx?: CSSProperties;
  autoTrigger?: boolean;
  product: string;
  useLink?: boolean;
  ubaSource?: string;
};

const PayuForm = ({
  title,
  refCode,
  pageId,
  tracking: {action, category},
  sx,
  autoTrigger = false,
  product,
  useLink = false,
  ubaSource: ubaSourceOverride,
}: PayuFormType) => {
  const router = useRouter();
  const theme = useWrappedTheme();
  const [loginOpen, setLoginOpen] = useState(false);
  const LoginForm = dynamic(
    () =>
      import(
        '@jobseeker/client-harness/components/AuthenticationHarness/AuthForm'
      ),
    {
      loading: () => <Loader />,
      ssr: false,
    }
  );
  const isCSR = () => {
    return typeof window !== 'undefined';
  };

  const onLoginSuccess = () => {
    if (isCSR()) {
      setLoginOpen(false);
      window.location.reload();
    }
  };
  const {
    profile,
    profileLoading,
  } = useSelector((state: AppState) => state.user);
  const {name, email, phone} = profile;
  const showRecruiterActions = profile?.proMembershipFeatures?.statusChange || false;


  const isBasicStyleProduct = [
    PaidProducts.EARLY_APPLICANT,
    PaidProducts.RESUME_BUILDER,
  ].includes(product);

  const handleButtonClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (profileLoading) {
      return;
    }

    handleTracking(action, {
      event_category: category,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Ref: `pro_buy_now_${refCode}`,
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });

    const eventUbaSource = ubaSourceOverride || getUbaSource(router?.query);

    TrackEventUBA('proMembershipPageClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      page: window.location.pathname,
      source: eventUbaSource,
      clickType: 'Buy Now',
    });

    const requestBodyParams = {
      product: product,
      pageId: pageId,
      locRefId: refCode,
      pageUid: 0,
      serviceProvider: PaymentGateway.PAYU,
      cpm: 'a',
    };

    handleUpgrade(requestBodyParams);
  };

  const handleButtonAction = () => {
    if (profileLoading) {
      return;
    }

    handleTracking(action, {
      event_category: category,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Ref: `pro_buy_now_${refCode}`,
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });

    const eventUbaSource = ubaSourceOverride || getUbaSource(router?.query);

    TrackEventUBA('proMembershipPageClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      page: window.location.pathname,
      source: eventUbaSource,
      clickType: 'Buy Now',
    });

    const requestBodyParams = {
      product: product,
      pageId: pageId,
      locRefId: refCode,
      pageUid: 0,
      serviceProvider: PaymentGateway.PAYU,
      cpm: 'a',
    };

    handleUpgrade(requestBodyParams);
  };

  useEffect(() => {
    const delay = 500;
    const timer = setTimeout(() => {
      if (autoTrigger) {
        if (!email) {
          setLoginOpen(true);
          return;
        }

        handleUpgrade({
          product: product,
          pageId,
          locRefId: refCode,
          pageUid: 0,
          serviceProvider: PaymentGateway.PAYU,
          cpm: 'a',
        });
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [autoTrigger, email]);

  const handleUpgrade = async (requestBodyParams: any) => {
    if (!email) {
      setLoginOpen(true);
      return;
    }

    const userFirstName = name?.split(' ')[0];
    const userPhone = phone || '1234567898';
    const userEmail = email;
    const requestData = {
      firstname: userFirstName,
      email: userEmail,
      phone: userPhone,
    };
    const response = (await subscriptionService.initiatePaymentGateway(
      requestBodyParams
    )) as initiatePaymentGatewayApiResponse;
    if (!response || (response as {error?: any}).error) {
      throw new Error('Failed to initiate payment. Please try again.');
    }
    const {
      paymentHash,
      txnId,
      successUrl,
      failureUrl,
      key,
      amount,
      productInfo,
      serviceProvider,
      formAction,
    } = response;
    // const successUrl = 'https://local.hirist.tech:3030/api/payu';
    // const failureUrl = 'https://local.hirist.tech:3030/api/payu';
    const form = document.createElement('form');
    form.action = `${formAction}`;
    form.method = 'POST';

    const hashData = {
      key,
      txnid: txnId,
      amount: amount,
      productinfo: productInfo,
      firstname: requestData.firstname,
      email: requestData.email,
      phone: requestData.phone,
      surl: successUrl,
      furl: failureUrl,
      service_provider: serviceProvider,
      hash: paymentHash,
    };

    sessionStorage.setItem('paymentData', JSON.stringify(hashData.productinfo));

    Object.entries(hashData).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <>
      {useLink ? (
        <a
          href="#"
          onClick={e => handleButtonClick(e)}
          style={{color: theme.palette.primary.main}}
        >
          use this link
        </a>
      ) : (
        <Button
          sx={{
            ...sx,
            background: sx?.background || theme.palette.pro.main,
            width: sx?.width,
            py: 1,
            px: isBasicStyleProduct ? {xxs: 2, xs: 0.5, md: 1.5} : sx?.px,
            mt: 1,
            mb: {xxs: 4, xs: 0, sm: 0},
            fontSize: sx?.fontSize || theme.typography.pxToRem(12),
            letterSpacing: '0.48px',
            fontWeight: theme.typography.fontWeightBold,
          }}
          key="buy_now_pro"
          data-testid="pro_membership_popup_buy_now"
          variant="contained"
          size="small"
          onClick={handleButtonAction}
        >
          {typeof title === 'string' && title.includes('for') ? (
            <>
              <Typography
                component="span"
                sx={{fontSize: '18px', fontWeight: 'bold', lineHeight: 1.2}}
              >
                {title.split('for')[0].trim()}
              </Typography>
              <Typography
                component="span"
                sx={{fontSize: '16px', lineHeight: 1.2, marginLeft: '5px'}}
              >
                for {title.split('for')[1].trim()}
              </Typography>
            </>
          ) : (
            <Typography component="span" sx={{fontSize: '12px'}}>
              {title}
            </Typography>
          )}
        </Button>
      )}
      {loginOpen && (
        <Dialog
          title="Login to your account"
          subtitle="Welcome back! 👋"
          invertedTitle
          variant={VersionVariants.VERSION_TWO}
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
        >
          <LoginForm
            onSuccess={onLoginSuccess}
            variant={VersionVariants.VERSION_TWO}
          />
        </Dialog>
      )}
    </>
  );
};

export default PayuForm;
